import React, {
  useEffect, useRef, useState,
} from 'react';

import { useSearchParams } from 'react-router-dom';

import { NoContentMessage, NoPermissionMessage } from '../../components/PageLayout';
import SkeletonStack from '../../components/SkeletonStack';
import Tabs from '../../components/Tabs';
import VideoCategoryCard from '../../components/VideoCategoryCard';
import { useGetVideoCategoriesQuery } from '../../services/videos';
import { Category } from '../../services/videos/types';
import { PermissionType } from '../../types/common';
import {
  SubjectDetails, SUBJECTS,
} from '../../utils/common/constants';
import getSubjectsFromPermissions from '../../utils/common/getSubjectsFromPermissions';
import useSnackbar from '../../utils/react/hooks/useSnackbar';
import useUserPermissions from '../../utils/react/hooks/useUserPermissions';

import {
  CategoriesContainer,
  SkeletonContainer,
  VideoCategoryCardContainer,
} from './index.styles';

interface CategoriesProps {
  categories: Category[],
  getVideoHref: (subject: string, videoId: string) => string,
  onCategoryClick: () => void,
  currSubject: string | null,
  currCategory: string | null,
  currTopic: string | null,
  containerRef: any,
}

const Categories = ({ categories,
  getVideoHref,
  onCategoryClick,
  currSubject,
  currCategory,
  currTopic,
  containerRef } : CategoriesProps) =>
  (categories.length > 0 ? (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {currSubject && categories?.map((c) => (
        <VideoCategoryCardContainer
          key={c.category}
          ref={containerRef}
        >
          <VideoCategoryCard
            color={SUBJECTS[currSubject]?.colorKey}
            categoryLabel={c.label}
            topics={c.topics}
            onCategoryClick={onCategoryClick}
            getVideoHref={getVideoHref}
            openOnLoad={currCategory === c.category && currTopic ? currTopic : false}
          />
        </VideoCategoryCardContainer>
      ))}
    </>
  ) : (<NoContentMessage />));

const VideosPage = () => {
  const createSnackbar = useSnackbar();
  const permissions = useUserPermissions();

  // Get parameters
  const [searchParams, setSearchParams] = useSearchParams();
  const errorCode = searchParams.get('err');
  const querySubject = searchParams.get('subject');
  const queryCategory = searchParams.get('category');
  const queryTopic = searchParams.get('topic');

  // Handle states
  const [subjectsList, setSubjectsList] = useState<SubjectDetails[] | null>(
    permissions ? getSubjectsFromPermissions(permissions, PermissionType.Video) : null,
  );

  // Handle parameter states
  const [subject, setSubject] = useState(querySubject || subjectsList?.[0]?.key || '');
  const [category, setCategory] = useState(queryCategory);
  const [topic, setTopic] = useState(queryTopic);

  // Fetch data
  const { data: categories, isFetching, error } = useGetVideoCategoriesQuery({ subject }, {
    skip: !subject,
  });

  /**
   * SUBJECTS LIST
   */

  useEffect(() => {
    const updateSubjects = async () => {
      if (permissions) {
        const list = await getSubjectsFromPermissions(permissions, PermissionType.Video);
        setSubjectsList(list);
        setSubject(querySubject || list[0]?.key);
      }
    };
    updateSubjects();
  }, [permissions]);

  /**
   * PARAMETER HANDLING
   */

  // Handle error code from parameter
  useEffect(() => {
    if (errorCode === 'no-video') {
      createSnackbar('Video could not be found', 'error');
    } else if (errorCode === 'no-permission') {
      createSnackbar('You are not allowed to watch this video', 'error');
    }
  }, [errorCode]);

  // If subject is specified
  useEffect(() => {
    if (querySubject && Object.keys(SUBJECTS).includes(querySubject)) {
      setSubject(querySubject);
    }
  }, [querySubject]);

  // If category is specified
  useEffect(() => {
    if (queryCategory) {
      setCategory(queryCategory);
    }
  }, [queryCategory]);

  // If topic is specified
  useEffect(() => {
    if (queryTopic) {
      setTopic(queryTopic);
    }
  }, [queryTopic]);

  /**
   * SCROLLING
   */

  const isPermissionError = error && 'status' in error && error.status === 401;

  // If the category is specified, scroll to it
  const categoriesRef = useRef<any>([]);
  useEffect(() => {
    if (subjectsList && categories && category) {
      const categoryIndex = categories?.data.map((c: Category) => c.category).indexOf(category);
      if (categoryIndex >= 0) {
        // If the parameter-specified category is on the screen currently
        const categoryDiv = categoriesRef.current[categoryIndex];
        // -48 is an offset to leave a bit of space at the top of the window
        const y = categoryDiv.getBoundingClientRect().top + window.pageYOffset - 96;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  }, [subjectsList, category, categories]);

  const resetCategory = () => {
    setCategory(null);
    setTopic(null);

    // Clear search params
    setSearchParams({}, { replace: true });
  };

  /**
   * RENDERING
   */

  if (subjectsList && subjectsList?.length === 0) {
    // If subjectsList has loaded but there are no subjectsList, show no permission message
    return (
      <NoPermissionMessage />
    );
  }

  if (!subjectsList || !permissions) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <>
      <Tabs
        options={subjectsList}
        selectedKey={subject}
        onChange={(newKey) => {
          setSubject(newKey);
          // Reset category to prevent unwanted scrolls
          resetCategory();
        }}
      />
      <CategoriesContainer>
        {!isFetching && isPermissionError && <NoPermissionMessage />}
        {!isFetching && !isPermissionError && (
          <Categories
            containerRef={(el: any) => categoriesRef.current.push(el)}
            categories={[...(categories?.data || [])]
              .sort((a, b) => a.label.localeCompare(b.label))}
            currSubject={subject}
            currCategory={category}
            currTopic={topic}
            getVideoHref={(subj, videoId) => `/video/${subj}/${videoId}`}
            onCategoryClick={() => {
            // Reset category to prevent unwanted scrolls
              resetCategory();
            }}
          />
        )}
        {isFetching && (
          <SkeletonContainer>
            <SkeletonStack />
          </SkeletonContainer>
        )}
      </CategoriesContainer>
    </>
  );
};
export default VideosPage;
